/**
 * One-shot duck for the post-signup community profile prompt. Wraps
 * the same SDK calls ProfileSettingsPage uses (uploadImage,
 * updateProfile), but also flips
 * `privateData.communityProfilePromptDone: true` on every Save *and*
 * Skip so the prompt is never shown again.
 */

import { storableError } from '../../util/errors';
import { uploadMemberAvatar } from '../../ducks/googleStorage.duck';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';

export const IMAGE_UPLOAD_REQUEST = 'app/CommunityProfilePromptPage/IMAGE_UPLOAD_REQUEST';
export const IMAGE_UPLOAD_SUCCESS = 'app/CommunityProfilePromptPage/IMAGE_UPLOAD_SUCCESS';
export const IMAGE_UPLOAD_ERROR = 'app/CommunityProfilePromptPage/IMAGE_UPLOAD_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/CommunityProfilePromptPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/CommunityProfilePromptPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/CommunityProfilePromptPage/UPDATE_PROFILE_ERROR';

export const SKIP_REQUEST = 'app/CommunityProfilePromptPage/SKIP_REQUEST';
export const SKIP_SUCCESS = 'app/CommunityProfilePromptPage/SKIP_SUCCESS';
export const SKIP_ERROR = 'app/CommunityProfilePromptPage/SKIP_ERROR';

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  skipInProgress: false,
  skipError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case IMAGE_UPLOAD_REQUEST:
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case IMAGE_UPLOAD_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      return {
        ...state,
        image: { id, imageId: uploadedImage.id, file, uploadedImage },
        uploadInProgress: false,
        uploadImageError: null,
      };
    }
    case IMAGE_UPLOAD_ERROR:
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };

    case UPDATE_PROFILE_REQUEST:
      return { ...state, updateInProgress: true, updateProfileError: null };
    case UPDATE_PROFILE_SUCCESS:
      return { ...state, image: null, updateInProgress: false };
    case UPDATE_PROFILE_ERROR:
      return { ...state, updateInProgress: false, updateProfileError: payload };

    case SKIP_REQUEST:
      return { ...state, skipInProgress: true, skipError: null };
    case SKIP_SUCCESS:
      return { ...state, skipInProgress: false };
    case SKIP_ERROR:
      return { ...state, skipInProgress: false, skipError: payload };

    default:
      return state;
  }
}

// ─── Action creators ────────────────────────────────────────────────

export const uploadImageRequest = params => ({ type: IMAGE_UPLOAD_REQUEST, payload: { params } });
// payload: { id: 'tempId', uploadedImage }
export const uploadImageSuccess = (id, uploadedImage) => ({
  type: IMAGE_UPLOAD_SUCCESS,
  payload: { id, uploadedImage },
});
export const uploadImageError = error => ({ type: IMAGE_UPLOAD_ERROR, payload: error, error: true });

export const updateProfileRequest = () => ({ type: UPDATE_PROFILE_REQUEST });
export const updateProfileSuccess = result => ({ type: UPDATE_PROFILE_SUCCESS, payload: result.data });
export const updateProfileError = e => ({ type: UPDATE_PROFILE_ERROR, payload: e, error: true });

export const skipRequest = () => ({ type: SKIP_REQUEST });
export const skipSuccess = () => ({ type: SKIP_SUCCESS });
export const skipError = e => ({ type: SKIP_ERROR, payload: e, error: true });

// ─── Thunks ─────────────────────────────────────────────────────────

export const uploadImage = actionPayload => (dispatch, getState, sdk) => {
  const id = actionPayload.id;
  dispatch(uploadImageRequest(actionPayload));

  // Stored in our own storage, not as a Sharetribe image (util/memberAvatar.js).
  return dispatch(uploadMemberAvatar(actionPayload.file))
    .then(uploadedImage => {
      dispatch(uploadImageSuccess(id, uploadedImage));
    })
    .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
};

// Always merges `communityProfilePromptDone: true` into privateData so
// any successful write — Save *or* Skip — disables future prompts.
const writeProfile = (dispatch, sdk, actionPayload) => {
  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };
  const params = {
    ...actionPayload,
    privateData: {
      ...(actionPayload?.privateData || {}),
      communityProfilePromptDone: true,
    },
  };
  return sdk.currentUser.updateProfile(params, queryParams).then(response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    dispatch(currentUserShowSuccess(entities[0]));
    return response;
  });
};

export const updateProfile = actionPayload => (dispatch, getState, sdk) => {
  dispatch(updateProfileRequest());
  return writeProfile(dispatch, sdk, actionPayload)
    .then(response => dispatch(updateProfileSuccess(response)))
    .catch(e => {
      dispatch(updateProfileError(storableError(e)));
      throw e;
    });
};

export const skipPrompt = () => (dispatch, getState, sdk) => {
  dispatch(skipRequest());
  return writeProfile(dispatch, sdk, {})
    .then(() => dispatch(skipSuccess()))
    .catch(e => {
      dispatch(skipError(storableError(e)));
      throw e;
    });
};
